import React, { useState, useEffect } from 'react';
import { useSetOnboardingStage, useCryptoOnboardingComplete, useStripeOnboardingStatus } from '../state';
import { updateOnboardingStage } from '../services';
import FiatOnboarding from './FiatOnboarding';
import CryptoOnboarding from './CryptoOnboarding';
import Loading from './Loading';
import Logout from './auth/Logout';
import PageLayout from './PageLayout';
import { useNavigate } from 'react-router-dom';
import { ONBOARDING_ROUTES, GENERAL_ROUTES } from '../constants/routes';
import bitcoinIcon from '../assets/icons/bitcoin-simple.svg';
import currenciesIcon from '../assets/icons/3currencies.png';

const FiatCryptoOnboarding = () => {
    const [showStripeOnboarding, setShowStripeOnboarding] = useState(false);
    const [showCryptoOnboarding, setShowCryptoOnboarding] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const setOnboardingStage = useSetOnboardingStage();
    const cryptoOnboardingComplete = useCryptoOnboardingComplete();
    const stripeOnboardingStatus = useStripeOnboardingStatus();
    const navigate = useNavigate();

    useEffect(() => {
        // If both onboarding steps are complete, navigate to home
        const completeOnboarding = async () => {
            try {
                if (stripeOnboardingStatus === 'complete' && cryptoOnboardingComplete) {
                    await updateOnboardingStage('complete');
                    setOnboardingStage('complete');
                    navigate(GENERAL_ROUTES.HOME);
                }
            } catch (error) {
                console.error('Error completing onboarding:', error);
                navigate(GENERAL_ROUTES.ERROR);
            }
        };

        completeOnboarding();
    }, [stripeOnboardingStatus, cryptoOnboardingComplete, navigate, setOnboardingStage]);

    const handleFiatClick = () => {
        setShowStripeOnboarding(true);
        setShowCryptoOnboarding(false);
    };

    const handleCryptoClick = () => {
        setShowStripeOnboarding(false);
        setShowCryptoOnboarding(true);
    };

    const handleBack = async () => {
        setIsLoading(true);
        try {
            await updateOnboardingStage('select-money-type');
            setOnboardingStage('select-money-type');
            navigate(ONBOARDING_ROUTES.SELECT_MONEY_TYPE);
        } catch (error) {
            console.error('Error going back:', error);
            navigate(GENERAL_ROUTES.ERROR);
        }
        setIsLoading(false);
    };

    if (isLoading) {
        return <Loading />;
    }

    return (
        <PageLayout>
            <div className="flex flex-col items-center justify-center min-h-[calc(100vh)]">
                {(!showStripeOnboarding && !showCryptoOnboarding) ? (
                    <>
                        <div className="w-full max-w-2xl">
                            <div className=" p-8 w-full">
                                <h2 className="text-xl text-white font-semibold mb-6 text-center">Choose your payment method</h2>
                                <div className="flex flex-row gap-6 justify-center">
                                    <button
                                        className={`flex flex-col items-center rounded-lg p-6 hover:ring-2 hover:ring-white transition-all w-48 ${stripeOnboardingStatus === 'complete' ? 'bg-green-500/20' : ''}`}
                                        onClick={handleFiatClick}
                                        disabled={isLoading || stripeOnboardingStatus === 'complete'}
                                    >
                                        <div className="h-16 flex items-center mb-4">
                                            <img src={currenciesIcon} alt="National Currencies" className="w-16 h-16" />
                                        </div>
                                        <span className="text-white">Set up regular currency payments</span>
                                        {stripeOnboardingStatus === 'complete' && (
                                            <span className="text-green-400 text-sm mt-2">✓ Complete</span>
                                        )}
                                    </button>
                                    <button
                                        className="flex flex-col items-center rounded-lg p-6 hover:ring-2 hover:ring-white transition-all w-48"
                                        onClick={handleCryptoClick}
                                        disabled={isLoading}
                                    >
                                        <div className="h-16 flex items-center mb-4">
                                            <img src={bitcoinIcon} alt="Bitcoin" className="w-16 h-16" />
                                        </div>
                                        <span className="text-white">Set up bitcoin payments</span>
                                    </button>
                                </div>
                            </div>
                            <div className="flex justify-center mt-4">
                                <button
                                    type="button"
                                    onClick={handleBack}
                                    className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition duration-300"
                                    disabled={isLoading}
                                >
                                    Back
                                </button>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        {showStripeOnboarding && <FiatOnboarding autoContinue={true} />}
                        {showCryptoOnboarding &&
                            <CryptoOnboarding
                                setShowCryptoOnboarding={setShowCryptoOnboarding}
                                setShowStripeOnboarding={setShowStripeOnboarding}
                            />
                        }
                    </>
                )}
            </div>
        </PageLayout>
    );
};

export default FiatCryptoOnboarding;
